/**
 * The "Allgemein" content of the Bexio settings page.
 *
 * Presentation only: it owns no state and fetches nothing. Everything it renders
 * arrives as props from `BexioSettingsPage`, which is the orchestrator — the same
 * split `BexioSyncRunsPage` and its children use.
 *
 * EDITABLE FOR AN ADMIN, REPORTED TO EVERYONE ELSE (D2). A manager gets every
 * row as a plain value — not disabled inputs, which would read as "you may edit
 * this, just not now" when the truth is that the endpoint refuses them.
 *
 * A BEXIO ID BECOMES A PICKER ONLY ONCE ITS CATALOG IS READABLE. `currency_id`
 * (D3a), `default_tax_id` (D3b), `bank_account_id` (D3c), `payment_type_id`
 * (D3d) and `default_language_id` (D3e) are fed by real provider catalogs, so an
 * admin picks from what this mandant actually has. The one remaining id —
 * `target_user_id` — stays read-only for both roles: a technically valid but
 * factually wrong id would misconfigure real invoices, and nothing here can check
 * one yet.
 *
 * The bank-account row shows an ID AND A NAME AND NOTHING ELSE. No IBAN, no
 * QR-IBAN, no account number, no holder address — the backend never sends them,
 * because choosing which account an invoice references does not require
 * displaying the account. This is a picker, not a banking view.
 *
 * The language row is the same idea applied to a wider row: the provider also
 * sends an ISO 639-1 code and four date/number formatting fields, and none of
 * them arrive. The label is the mandant's own name for the language — never
 * "Sprache (xx)" composed on this side, which would drift from what the operator
 * reads inside Bexio. It selects the language of the BEXIO DOCUMENT, not the UI.
 *
 * THE SUMMARY STRIP ANSWERS STATUS QUESTIONS, NOT SETTINGS QUESTIONS (D3e.1).
 * Who the invoice is issued under, whether the link is alive, whether the
 * configuration is complete — three things an operator checks before working,
 * none of which needs a provider call. It carried a fourth card until D3e.1: the
 * bare `default_language_id`, printed as "1" under the hint "Bexio-Sprach-ID".
 * Before D3e that was the most honest thing available, because nothing could
 * resolve the id. After D3e the settings row below shows the provider's own name
 * for the same value, so the strip was left claiming "1" while the row said
 * "Deutsch" — one value, two presentations, the less readable one on top.
 *
 * The card is gone rather than resolved. Feeding it from `catalogs.languages`
 * would put a live Bexio request behind the always-visible summary, so a language
 * outage would blank or break a strip that has nothing to do with the provider;
 * the whole point of the per-catalog state below is that such an outage degrades
 * ONE dropdown. And a `1 -> "Deutsch"` table on this side is exactly the invention
 * D3e refused. Nothing was lost: the value is still on the page, once, in the row
 * that also lets an admin change it.
 *
 * The catalog rows are the ONLY thing on this page that depends on a live Bexio
 * call, and each has its own option list, state and error via `catalogs` — so a
 * tax outage leaves the currency picker working, and neither can blank the
 * configuration.
 *
 * WHAT THE DESIGN REFERENCE SHOWS AND THIS DOES NOT, deliberately: a payment-term
 * dropdown ("13 Tage nach Rechnungsdatum"), invoice/service-period date rules and
 * check-in/check-out pickers. Vantio's Bexio dates are ABSOLUTE — issue on the
 * 15th and due on the 28th of the preceding month — and are computed from the rent
 * period alone, with no setting behind them. A dropdown there would be inert at
 * best and would contradict the documented billing rule at worst. Per-scope sync
 * toggles and a per-row tax code are absent for the same reason: no such
 * persistence exists.
 */
import React from "react";
import { BadgeCheck, Link2, Users } from "lucide-react";

import BexioKpiFilterCard from "../../../../components/admin/bexio/workspace/BexioKpiFilterCard";
import {
  CONNECTION_STATUS_LABEL,
  SCOPE_LABEL,
  connectionTone,
  mapLabel,
} from "../../../../components/admin/bexio/bexioFormat";
import { blockerText } from "../../../../components/admin/bexio/bexioDraftLabels";
import { formatDateTime } from "../../../../utils/datetime";
import BexioSettingsField from "./BexioSettingsField";
import BexioSettingsControl from "./BexioSettingsControl";
import BexioCatalogSelect from "./BexioCatalogSelect";
import BexioTargetUserRow from "./BexioTargetUserRow";
import {
  CONFIG_FIELDS,
  completenessLabel,
  completenessTone,
  contactPersonLabel,
  formatConfigValue,
} from "../bexioSettingsView";

const CARD = "rounded-xl border border-border bg-card p-5";
const CARD_TITLE = "text-sm font-semibold text-foreground";

/** Placeholder shown while a card's own request is still in flight. */
const PENDING = "…";

export default function BexioSettingsGeneral({
  config,
  configLoading,
  status,
  statusLoading,
  form = null,
  fieldErrors = {},
  canEdit = false,
  onFieldChange = () => {},
  // Keyed by `field.catalog`, so a new catalog row needs no branch in here.
  catalogs = {},
  // D3f.2. The internal contact does not ride `form`/`onFieldChange`: its write
  // is a separate endpoint, so its draft, its dirty flag and its save live in
  // their own bundle. Passing it as one object keeps that separation visible at
  // the call site instead of scattering four more props among the shared ones.
  targetUser = null,
}) {
  const configured = config?.configured === true;
  const values = config?.values || {};
  const blockers = config?.missing_blockers || [];
  const missingSet = new Set(blockers);
  const lastSync = status?.last_successful_sync || null;
  const counts = status?.counts || null;

  return (
    <>
      {/* ── Summary strip ──────────────────────────────────────────── */}
      <section
        aria-label="Konfigurationsüberblick"
        className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
      >
        <BexioKpiFilterCard
          icon={Users}
          tone="primary"
          label="Ansprechpartner"
          value={configLoading ? PENDING : contactPersonLabel(values)}
        />
        <BexioKpiFilterCard
          icon={Link2}
          tone={configured ? connectionTone(config?.connection_status) : "neutral"}
          label="Verbindung"
          value={
            configLoading
              ? PENDING
              : configured
              ? mapLabel(CONNECTION_STATUS_LABEL, config?.connection_status)
              : "Nicht konfiguriert"
          }
        />
        <BexioKpiFilterCard
          icon={BadgeCheck}
          tone={configLoading ? "neutral" : completenessTone(config)}
          label="Konfiguration"
          value={configLoading ? PENDING : completenessLabel(config)}
        />
      </section>

      <div className="grid gap-4 xl:grid-cols-3">
        {/* ── Verbindung ───────────────────────────────────────────── */}
        <section aria-label="Verbindung" className={`${CARD} xl:col-span-1`}>
          <h2 className={CARD_TITLE}>Verbindung</h2>
          <dl className="mt-2">
            <BexioSettingsField
              label="Status"
              value={
                configLoading
                  ? PENDING
                  : configured
                  ? mapLabel(CONNECTION_STATUS_LABEL, config?.connection_status)
                  : "Keine Verbindung hinterlegt"
              }
            />
            <BexioSettingsField
              label="Letzte erfolgreiche Synchronisation"
              value={
                statusLoading
                  ? PENDING
                  : lastSync?.finished_at
                  ? formatDateTime(lastSync.finished_at)
                  : "—"
              }
              hint={
                lastSync?.entity_scope
                  ? mapLabel(SCOPE_LABEL, lastSync.entity_scope)
                  : undefined
              }
            />
            <BexioSettingsField
              label="Gespiegelte Datensätze"
              value={
                statusLoading
                  ? PENDING
                  : counts
                  ? `${counts.contacts} Kontakte · ${counts.invoices} Rechnungen · ${counts.payments} Zahlungen`
                  : "—"
              }
            />
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Synchronisieren lässt sich die Verbindung auf der Übersicht.
          </p>
        </section>

        {/* ── Rechnungskonfiguration ───────────────────────────────── */}
        <section
          aria-label="Rechnungskonfiguration"
          className={`${CARD} xl:col-span-2`}
        >
          <h2 className={CARD_TITLE}>Rechnungskonfiguration</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Diese Werte gehen in jeden Bexio-Rechnungsentwurf ein.
            Providerabhängige Auswahlfelder werden aus Bexio übernommen.
          </p>
          <dl className="mt-2 grid gap-x-6 md:grid-cols-2">
            {CONFIG_FIELDS.map((field) => {
              const editable = canEdit && field.control && form;
              if (!editable) {
                return (
                  <BexioSettingsField
                    key={field.key}
                    label={field.label}
                    hint={field.hint}
                    missing={missingSet.has(field.blocker)}
                    value={
                      configLoading ? PENDING : formatConfigValue(values[field.key])
                    }
                  />
                );
              }
              if (field.control === "targetUser") {
                // The editable row needs the page's target-user bundle. Without
                // it, fall back to REPORTING the stored value — never to
                // rendering nothing: this is a required config field, and a row
                // that silently vanishes would read as "not applicable" when the
                // truth is "still set, just not editable here".
                if (!targetUser) {
                  return (
                    <BexioSettingsField
                      key={field.key}
                      label={field.label}
                      hint={field.hint}
                      missing={missingSet.has(field.blocker)}
                      value={
                        configLoading
                          ? PENDING
                          : formatConfigValue(values[field.key])
                      }
                    />
                  );
                }
                const catalog = catalogs[field.catalog] || {};
                return (
                  <BexioTargetUserRow
                    key={field.key}
                    id={`bexio-config-${field.key}`}
                    label={field.label}
                    hint={field.hint}
                    value={targetUser.draft}
                    options={catalog.options || []}
                    catalogState={catalog.state || "idle"}
                    catalogError={catalog.error || ""}
                    onRetry={catalog.onRetry}
                    missing={missingSet.has(field.blocker)}
                    dirty={targetUser.dirty}
                    saving={targetUser.saving}
                    saveError={targetUser.error}
                    onChange={targetUser.onChange}
                    onSave={targetUser.onSave}
                  />
                );
              }
              if (field.control === "catalog") {
                const catalog = catalogs[field.catalog] || {};
                return (
                  <BexioCatalogSelect
                    key={field.key}
                    id={`bexio-config-${field.key}`}
                    label={field.label}
                    hint={field.hint}
                    value={form[field.key]}
                    options={catalog.options || []}
                    catalogState={catalog.state || "idle"}
                    catalogError={catalog.error || ""}
                    onRetry={catalog.onRetry}
                    missing={missingSet.has(field.blocker)}
                    onChange={(next) => onFieldChange(field.key, next)}
                  />
                );
              }
              return (
                <BexioSettingsControl
                  key={field.key}
                  id={`bexio-config-${field.key}`}
                  label={field.label}
                  hint={field.hint}
                  control={field.control}
                  value={form[field.key]}
                  error={fieldErrors[field.key]}
                  missing={missingSet.has(field.blocker)}
                  onChange={(next) => onFieldChange(field.key, next)}
                />
              );
            })}
          </dl>
        </section>
      </div>

      {/* ── Konfigurationsprüfung ──────────────────────────────────── */}
      <section aria-label="Konfigurationsprüfung" className={`${CARD} mt-4`}>
        <h2 className={CARD_TITLE}>Konfigurationsprüfung</h2>
        {configLoading ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Konfiguration wird geprüft …
          </p>
        ) : blockers.length === 0 ? (
          <p className="mt-2 flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-400">
            <BadgeCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Die Bexio-Rechnungskonfiguration ist vollständig. Entwürfe werden
              dadurch nicht blockiert.
            </span>
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground">
              Solange eine dieser Angaben fehlt, blockiert Vantio die Erstellung von
              Bexio-Entwürfen.
            </p>
            <ul className="mt-3 space-y-2">
              {blockers.map((token) => (
                <li
                  key={token}
                  className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                >
                  <span aria-hidden="true" className="mt-0.5 shrink-0 font-semibold">
                    !
                  </span>
                  <span className="min-w-0 break-words">{blockerText(token)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  );
}
